import { onPageScroll, prefersReducedMotion } from '@lucid/core';

/* Scroll-linked scale on the hero media: 1 at the top, up to 1 + depth once the hero has scrolled out. */
class LucidHero extends HTMLElement {
  #frame = 0;
  #active = false;
  #observer = null;
  #offScroll = null;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.media = this.querySelector('[data-lucid-hero-scale]');
    if (!this.media || !this.hasAttribute('scroll-scale')) return;

    this.depth = Number.parseFloat(this.getAttribute('scroll-scale')) || 0.08;
    this.onScroll = () => this.#schedule();
    this.onMotion = () => this.#apply();

    this.#observer = new IntersectionObserver((entries) => {
      this.#active = entries.some((entry) => entry.isIntersecting);
      this.#offScroll?.();
      this.#offScroll = null;
      if (this.#active) {
        this.#offScroll = onPageScroll(this.onScroll);
        this.#schedule();
      }
    });
    this.#observer.observe(this);
    this.#motionQuery.addEventListener('change', this.onMotion);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#offScroll?.();
    this.#offScroll = null;
    this.#motionQuery.removeEventListener('change', this.onMotion);
    cancelAnimationFrame(this.#frame);
  }

  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#apply();
    });
  }

  #apply() {
    if (prefersReducedMotion()) {
      this.media.style.removeProperty('--lc-hero-progress');
      return;
    }
    const rect = this.getBoundingClientRect();
    const progress = Math.min(Math.max(-rect.top / Math.max(rect.height, 1), 0), 1);
    this.media.style.setProperty('--lc-hero-progress', progress.toFixed(4));
    this.media.style.setProperty('--lc-hero-depth', String(this.depth));
  }
}

if (!customElements.get('lucid-hero')) customElements.define('lucid-hero', LucidHero);
